import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router';

import ContributeCounter from '@/components/ContributeCounter';
import PageShell from '@/components/PageShell';
import PageCTA from '@/components/PageCTA';
import LandingContext, {
  Copyright as LandingCopyright,
  GrowthCard,
  MobileGiftBox,
  MobileSignatureBar,
  PartnerBar,
  // PrivateToggle,
  TrustTiles,
} from '@/components/LandingContext';
import MemoryBox from '@/components/MemoryBox';
import { useGift } from '@/tsq/queries';
import { Spinner } from '@/components/ui/spinner';
import {
  getGiftInviteCode,
  useSetGiftInviteCode,
  useGoalType,
  useGiftName,
  useSetGiftName,
  useSetGoalType,
  useGiftImageUrl,
  useSetGiftImage,
  useChildFirstName,
  useSetChildFirstName,
  useShowChildPhoto,
  useSetShowChildPhoto,
  useResetStore,
  useAmount,
  usePrivateAmount,
  useSetAmount,
  useSetContributionId,
  useContributionId,
  useOpenBankingId,
  useOpenBankingURL,
} from '@/store';
import { formatGiftTitle } from '@/utils/formatters/giftTitle';
import type { goalType } from '@/types';
import { blobStorage } from '@/utils/helpers/storage.helpers';
import { STEP_SIZE, MINIMUM_AMOUNT, MAXIMUM_AMOUNT } from '@/constants';
import { parsePaymentId } from '@/utils/helpers/payment.helpers';
import { validateAmount } from '@/utils/helpers/amount.helpers';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircleIcon, InfoIcon } from 'lucide-react';

import Button from '@/components/ui/button';

import {
  ConfirmSheet,
  ConfirmSheetAction,
  ConfirmSheetCancel,
  ConfirmSheetContent,
  ConfirmSheetDescription,
  ConfirmSheetFooter,
  ConfirmSheetTitle,
} from '@/components/ui/sheet';

import { formatCurrency } from '@/utils/formatters/currency';
import { giftContribute } from '@/api/gifting/gifting';
import { useMutation } from '@tanstack/react-query';
import VisuallyHidden from '@/components/VisuallyHidden/VisuallyHidden';
import { miaConsoleLog } from '@/utils/mia-logger';
import { amountBand, track } from '@/analytics';

import useIsOnline from '@/hooks/useIsOnline';

export const giftCodeURLParamKey = 'code';

function SendGift() {
  const isOnline = useIsOnline();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const code = getGiftInviteCode();
  const amount = useAmount();
  const privateAmount = usePrivateAmount();
  const setContributionId = useSetContributionId();

  const { mutateAsync: contributeGift } = useMutation({
    mutationFn: (payload: {
      amount: number;
      privateAmount: boolean;
      code: string;
    }) => giftContribute(payload),
    onSuccess: () => {},
    onError: () => {},
  });

  const resetStore = useResetStore();
  const storedGiftInviteCode = getGiftInviteCode();
  const storedGiftName = useGiftName();
  const storedGoalType = useGoalType();
  const storedGiftImageUrl = useGiftImageUrl();
  const storedChildFirstName = useChildFirstName();
  const storedShowChildPhoto = useShowChildPhoto();

  const setGiftInviteCode = useSetGiftInviteCode();
  const setAmount = useSetAmount();
  const setGiftName = useSetGiftName();
  const setGoalType = useSetGoalType();
  const setGiftImage = useSetGiftImage();
  const setChildFirstName = useSetChildFirstName();
  const setShowChildPhoto = useSetShowChildPhoto();

  const storedContributionId = useContributionId();
  const storedPaymentId = useOpenBankingId();
  const storedAuthoriseUrl = useOpenBankingURL();

  const [searchParams, setSearchParams] = useSearchParams();
  const giftInviteCodeFromURL = searchParams.get(giftCodeURLParamKey);
  const resetRequired = searchParams.get('reset') === 'true';

  const [inviteExhausted, setInviteExhausted] = useState(false);
  const [contributeError, setContributeError] = useState<string | null>(null);

  // The stored gift differs from the one in the link: ask which to continue.
  // Derived, so the dialog closes by itself once either action resolves the
  // mismatch (no setState-in-effect).
  const skipDialogOpen = Boolean(
    giftInviteCodeFromURL &&
      storedGiftInviteCode &&
      giftInviteCodeFromURL !== storedGiftInviteCode,
  );

  // ALL hooks must be called before any early returns

  // Get gift data unconditionally — must not be called after any early return
  const { data, error, isLoading } = useGift(giftInviteCodeFromURL || '');

  const startNewGift = useCallback(async () => {
    resetStore();
    await blobStorage.clear();
    setGiftInviteCode(giftInviteCodeFromURL as string);
  }, [resetStore, setGiftInviteCode, giftInviteCodeFromURL]);

  const continueExistingGift = async () => {
    setSearchParams({ [giftCodeURLParamKey]: storedGiftInviteCode });
    navigate(0);
  };

  /**
   * `?reset=true` must fire once. `startNewGift` clears the store and only
   * restores the invite code after an await, so the effect's dependency on the
   * stored code flips mid-flight and re-triggers the reset — which hangs the
   * tab. Found while building AC 10, which needs reset to beat resume.
   */
  const hasReset = useRef(false);

  // Redirect if no codes present, or handle reset
  useEffect(() => {
    if (!giftInviteCodeFromURL && !storedGiftInviteCode) {
      // About Us renders this, so it replaces the toast rather than needing one.
      (async () => {
        await navigate('/about-us', {
          state: { message: t('no-gift-code-found') },
          replace: true,
        });
      })();
    }
    if (resetRequired && !hasReset.current) {
      hasReset.current = true;
      startNewGift();
    }
  }, [
    giftInviteCodeFromURL,
    storedGiftInviteCode,
    navigate,
    t,
    resetRequired,
    startNewGift,
  ]);

  // Adopt the code from the link when there is nothing stored yet
  useEffect(() => {
    if (giftInviteCodeFromURL && !storedGiftInviteCode) {
      setGiftInviteCode(giftInviteCodeFromURL);
    }
  }, [giftInviteCodeFromURL, storedGiftInviteCode, setGiftInviteCode]);

  useEffect(() => {
    if (!skipDialogOpen) return;
    console.warn(
      `Gift invite code in URL (${giftInviteCodeFromURL}) does not match stored gift invite code (${storedGiftInviteCode}). Prompting user to choose which gift to contribute to.`,
    );
  }, [skipDialogOpen, giftInviteCodeFromURL, storedGiftInviteCode]);

  // Log gift query errors remotely. Deliberately keyed on `error` alone: one
  // log per failure, not one per invite-code change while it persists.
  useEffect(() => {
    if (!error) return;
    miaConsoleLog('error', {
      event: 'send_gift_load_error',
      giftInviteCode: giftInviteCodeFromURL ?? storedGiftInviteCode ?? null,
      errorMessage: error instanceof Error ? error.message : String(error),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  /**
   * Top of the funnel: a valid invite opened (GIFTWEB-33).
   *
   * Once per visit to this screen, not once per render, and only once the
   * invite has actually resolved — an invite that 404s never started a gift.
   * A resume counts, because from the funnel's side it is the same person
   * arriving at the same first step.
   */
  const giftStartTracked = useRef(false);
  useEffect(() => {
    if (!data || giftStartTracked.current) return;
    giftStartTracked.current = true;
    track('giftStarted');
  }, [data]);

  // Sync gift data to store
  useEffect(() => {
    if (!data) return;

    const firstName = data.data.child.firstName;
    const goalType = data.data.child.goalType as goalType;
    const fullGiftTitle = `${firstName}'s ${formatGiftTitle({ goalType: goalType as goalType })}`;
    const photoSignedUrl = data.data.child.photoSignedUrl;
    // Seconds, from the API. Stored with the URL so a later page can tell
    // whether the signature is still alive.
    const photoExpiresIn = data.data.child.photoExpiresIn as number | undefined;
    const shareChildProfilePhoto = Boolean(
      data.data.child.shareChildProfilePhoto,
    );

    if (fullGiftTitle !== storedGiftName) setGiftName(fullGiftTitle);
    if (goalType !== storedGoalType) setGoalType(goalType);
    if (photoSignedUrl !== storedGiftImageUrl)
      setGiftImage(photoSignedUrl, photoExpiresIn);
    if (firstName !== storedChildFirstName) setChildFirstName(firstName);
    if (shareChildProfilePhoto !== storedShowChildPhoto)
      setShowChildPhoto(shareChildProfilePhoto);
  }, [
    data,
    storedGiftName,
    storedGoalType,
    storedGiftImageUrl,
    storedChildFirstName,
    storedShowChildPhoto,
    setGiftName,
    setGoalType,
    setGiftImage,
    setChildFirstName,
    setShowChildPhoto,
  ]);

  // Early return AFTER all hooks
  if (!giftInviteCodeFromURL && !storedGiftInviteCode) {
    return null;
  }

  // AC 10 — a payment was left in flight, so pick it up rather than starting
  // over. Starting over would create a second contribution while the first is
  // still pending, which risks paying twice if the original settles late.
  //
  // Only for this gift: a link to a different child is a new journey, and the
  // mismatch dialog above already covers that. `?reset=true` still wins, and
  // the outcome screen clears the attempt so Back to the start can escape.
  const resumePaymentId =
    storedPaymentId || parsePaymentId(storedAuthoriseUrl ?? '');
  const isSameGift =
    !giftInviteCodeFromURL || giftInviteCodeFromURL === storedGiftInviteCode;

  if (resumePaymentId && storedContributionId && isSameGift && !resetRequired) {
    return (
      <Navigate
        to={`/openbanking/callback?payment_id=${encodeURIComponent(resumePaymentId)}`}
        replace
      />
    );
  }

  // Derived variables — no hooks, no setState below this point
  let usesRemaining = 0;
  if (data) {
    usesRemaining = data.data.invite?.usesRemaining ?? 0;
  }

  let firstName = '';
  let photoSignedUrl = '';
  let showChildProfilePhoto = false;
  let inviterFirstName = '';

  if (data) {
    firstName = data.data.child.firstName;
    photoSignedUrl = data.data.child.photoSignedUrl;
    showChildProfilePhoto = data.data.child.shareChildProfilePhoto;
    inviterFirstName = data.data.invite?.inviterFirstName ?? '';
  }

  const amountValidation = validateAmount(
    amount,
    MINIMUM_AMOUNT,
    MAXIMUM_AMOUNT,
  );

  const handleContribute = async () => {
    if (!amountValidation.valid) return;
    setContributeError(null);

    const payload = { amount, privateAmount, code };

    try {
      const res = await contributeGift(payload);
      const contributionId = res?.data?.contributionId;
      setContributionId(contributionId);

      track('amountSubmitted', {
        band: amountBand(amount),
        privateAmount: privateAmount ? 'yes' : 'no',
      });

      // V2 order: amount → details → message → payment. Pushed (not replaced)
      // so the details screen's back arrow / browser back returns here.
      navigate({ pathname: '/gift-details' });
    } catch (error) {
      // apiClient rejects with a normalized Error carrying `.status`
      const status = (error as Error & { status?: number }).status;
      const message =
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred during contribution.';

      console.error('Contribution error:', message);
      miaConsoleLog('error', {
        event: 'send_gift_contribution_error',
        giftInviteCode: code ?? null,
        amount,
        errorMessage: message,
      });

      // A 4xx means the invite can't take this contribution (already used up,
      // expired, revoked) — show the invalid-invite state instead of a toast.
      if (status && status >= 400 && status < 500 && status !== 401) {
        setInviteExhausted(true);
        return;
      }
      setContributeError(
        "We couldn't start your gift just then. Please try again in a moment.",
      );
    }
  };

  const showJourney = usesRemaining > 0 && data && !inviteExhausted;

  return (
    <>
      <PageShell
        variant='split'
        left={
          <>
            <div className='lg:hidden flex flex-col gap-4'>
              <MobileSignatureBar />
              {showJourney && (
                <>
                  <MobileGiftBox
                    firstName={firstName}
                    photoUrl={photoSignedUrl}
                    showPhoto={showChildProfilePhoto}
                    inviterName={inviterFirstName}
                  />
                  <MemoryBox childName={firstName} />
                </>
              )}
            </div>
            <div className='hidden lg:block'>
              <LandingContext
                firstName={firstName}
                photoUrl={photoSignedUrl}
                showPhoto={showChildProfilePhoto}
                inviterName={inviterFirstName}
              />
            </div>
          </>
        }
        right={
          <>
            {isLoading && (
              <div className='flex flex-col items-center justify-center'>
                <Spinner />
                <p>{t('loading-gift-data')}</p>
              </div>
            )}
            {error && (
              <Alert
                variant='destructive'
                className='mb-4 border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-50'
              >
                <AlertCircleIcon />
                <AlertDescription>
                  <p>
                    {t('error-loading-gift-data')}: {error.message}
                  </p>
                </AlertDescription>
              </Alert>
            )}
            {!error && data && (usesRemaining === 0 || inviteExhausted) && (
              <Alert className='mb-4 border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-50'>
                <InfoIcon />
                <AlertDescription>
                  <p>{t('no-uses-remaining')}</p>
                </AlertDescription>
              </Alert>
            )}
            {showJourney && (
              <>
                <div>
                  <div className='text-[16px] lg:text-[19px] font-bold text-left text-[#1a201d] mb-3'>
                    How much would you like to gift?
                  </div>
                  <ContributeCounter
                    amount={amount}
                    minimumAmount={MINIMUM_AMOUNT}
                    maximumAmount={MAXIMUM_AMOUNT}
                    stepValue={STEP_SIZE}
                    onChange={setAmount}
                  />
                </div>
                {/* <PrivateToggle /> */}
                <div className='lg:hidden'>
                  <GrowthCard childName={firstName} />
                </div>
                <div className='hidden lg:block'>
                  <MemoryBox childName={firstName} />
                </div>
                <div className='lg:hidden flex flex-col gap-5 mt-4'>
                  <TrustTiles variant='mobile' />
                  <PartnerBar variant='mobile' />
                  <LandingCopyright marginTop={4} />
                </div>
                <PageCTA>
                  {contributeError && (
                    <p role='alert' className='text-[13px] text-[#9f1239]'>
                      {contributeError}
                    </p>
                  )}
                  <Button
                    disabled={!isOnline || !amountValidation.valid}
                    className='w-full'
                    onClick={handleContribute}
                  >
                    <span>
                      {amount > 0
                        ? `Gift ${formatCurrency({ amount, precision: 0 })}`
                        : 'Gift'}
                      {firstName ? ` to ${firstName}` : ''}
                    </span>
                    {amountValidation.valid && (
                      <svg
                        width='18'
                        height='18'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2.2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        aria-hidden='true'
                        className='size-4.5!'
                      >
                        <path d='M5 12h14M13 6l6 6-6 6' />
                      </svg>
                    )}
                  </Button>
                  <p className='text-center text-[10.5px] font-bold text-[#8A938C]'>
                    Child's capital at risk.
                  </p>
                </PageCTA>
              </>
            )}
          </>
        }
      />
      <ConfirmSheet open={skipDialogOpen}>
        <ConfirmSheetContent>
          <ConfirmSheetTitle>
            It looks like you already started a gift for another child
          </ConfirmSheetTitle>
          <VisuallyHidden>
            <ConfirmSheetDescription>
              Contribute to a different gift?
            </ConfirmSheetDescription>
          </VisuallyHidden>
          <p>
            Would you like to carry on with that gift or start again with this
            new one? If you start again, your previous progress will be cleared
            and won't be saved.
          </p>
          <ConfirmSheetFooter>
            <ConfirmSheetCancel onClick={() => continueExistingGift()}>
              Continue previous gift
            </ConfirmSheetCancel>
            <ConfirmSheetAction onClick={() => startNewGift()}>
              Start again with this gift
            </ConfirmSheetAction>
          </ConfirmSheetFooter>
        </ConfirmSheetContent>
      </ConfirmSheet>
    </>
  );
}

export default SendGift;
